import { useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';

/**
 * Hook to claim a pending blueprint draft after user authentication.
 * 
 * This hook checks localStorage for a pending_blueprint_claim token
 * and attempts to claim it once the user is authenticated.
 * 
 * Usage: Place this hook in a component that renders after authentication,
 * such as the Dashboard or a layout component.
 */
export const useBlueprintClaim = (user, organizationId) => {
  const [claimStatus, setClaimStatus] = useState({
    claimed: false,
    loading: false,
    error: null,
    processId: null
  });

  useEffect(() => {
    const claimPendingBlueprint = async () => {
      // Check if there's a pending blueprint to claim
      const pendingToken = localStorage.getItem('pending_blueprint_claim');
      
      if (!pendingToken) {
        return;
      }

      // Only proceed if user is authenticated
      if (!user) {
        console.log('[BlueprintClaim] Waiting for authentication...');
        return;
      }

      console.log('[BlueprintClaim] Found pending blueprint to claim:', pendingToken);
      setClaimStatus(prev => ({ ...prev, loading: true }));

      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        if (organizationId) {
          headers['X-Organization-Id'] = organizationId;
        }

        const response = await fetch(`${BACKEND_URL}/api/blueprint-draft/claim`, {
          method: 'POST',
          headers,
          credentials: 'include',  // Use httpOnly cookie auth
          body: JSON.stringify({ draft_token: pendingToken })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to claim blueprint');
        }

        console.log('[BlueprintClaim] Successfully claimed blueprint:', data);
        
        // Clear the pending claim
        localStorage.removeItem('pending_blueprint_claim');
        
        setClaimStatus({
          claimed: true,
          loading: false,
          error: null,
          processId: data.process_id
        });

        // Return the process ID for navigation
        return data.process_id;
        
      } catch (error) {
        console.error('[BlueprintClaim] Error claiming blueprint:', error);
        
        // If the error is "already claimed" or "expired", clear the pending token
        if (error.message.includes('claimed') || error.message.includes('expired')) {
          localStorage.removeItem('pending_blueprint_claim');
        }
        
        setClaimStatus({
          claimed: false,
          loading: false,
          error: error.message,
          processId: null
        });
      }
    };

    claimPendingBlueprint();
  }, [user, organizationId]);

  return claimStatus;
};

export default useBlueprintClaim;
